// 約定結果タブ (AC-BSP-037) のフィルタ。
//
// フィルタは通常入札とブロック入札の両方に同じく効く。 ブロック入札はエリア・売買を Leg ごとに、
// コマを対象コマ範囲との重なりで、 ワークフローをグループを作ったワークフローの ID で判定する (KPI カード・エリア × 売買集計に選んだコマの分だけを
// 入れるのは `toBlockExecutionSummaryRecords`)。
import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import type { Side } from '@ebalance/proto/bfftypes/v1/side_pb'

import {
  NO_WORKFLOW,
  collectWorkflowIds,
  workflowFilterKey,
} from '~/models/workflowFilter'

import type { BlockBidRow } from './blockBidRow'
import { deriveBlockBidStatusCategory } from './blockBidStatusCategory'
import type { SpotBidRow } from './spotBidRow'
import {
  type SpotBidStatusCategory,
  deriveSpotBidStatusCategory,
} from './spotBidStatusCategory'

/** 約定結果タブのフィルタ。 どの軸も空集合は「すべて」。 */
export type SpotExecutionResultFilter = {
  komas: ReadonlySet<number>
  areas: ReadonlySet<AreaCode>
  sides: ReadonlySet<Side>
  statuses: ReadonlySet<SpotBidStatusCategory>
  /** 札の workflowId (業務ID) と「ワークフローなし」 (`NO_WORKFLOW`)。 */
  workflows: ReadonlySet<string>
}

const matchesAll = <T>(selected: ReadonlySet<T>, value: T): boolean =>
  selected.size === 0 || selected.has(value)

/** フィルタに合う通常入札の札。 */
export const filterNormalExecutionBids = (
  rows: readonly SpotBidRow[],
  filter: SpotExecutionResultFilter,
  isPastDeadline: boolean,
): SpotBidRow[] =>
  rows.filter(
    (row) =>
      !row.needsReview &&
      matchesAll(filter.komas, row.timeCode) &&
      matchesAll(filter.areas, row.areaCode) &&
      matchesAll(filter.sides, row.direction) &&
      matchesAll(filter.workflows, workflowFilterKey(row.workflowId)) &&
      matchesAll(
        filter.statuses,
        deriveSpotBidStatusCategory(row, isPastDeadline),
      ),
  )

/** フィルタに合うブロック入札の Leg。 コマは対象コマ範囲に選んだコマを含むかで見る。 */
export const filterBlockExecutionLegs = (
  rows: readonly BlockBidRow[],
  filter: SpotExecutionResultFilter,
  isPastDeadline: boolean,
): BlockBidRow[] =>
  rows.filter(
    (row) =>
      !row.needsReview &&
      (filter.komas.size === 0 ||
        row.slotCodes.some((code) => filter.komas.has(code))) &&
      matchesAll(filter.areas, row.areaCode) &&
      matchesAll(filter.sides, row.direction) &&
      matchesAll(filter.workflows, workflowFilterKey(row.workflowId)) &&
      matchesAll(
        filter.statuses,
        deriveBlockBidStatusCategory(row, isPastDeadline),
      ),
  )

/**
 * ワークフローの選択肢の値。 先頭に「ワークフローなし」 を固定し、 その後に受渡日の札 (通常入札・ブロック入札)
 * に現れる workflowId を昇順で並べる (AC-BSP-037 / AC-BSP-019。 時間前入札の AC-BIN-018 と同じ作り方)。
 */
export const executionResultWorkflowOptions = (
  bids: readonly SpotBidRow[],
  blockBidRows: readonly BlockBidRow[],
): string[] => [NO_WORKFLOW, ...collectWorkflowIds([...bids, ...blockBidRows])]

/**
 * 選んだワークフローのうち、 選択肢 (受渡日の札の ID と「ワークフローなし」) に無いものを外す。 他の日に
 * 選んだワークフローを復元すると 0 件に見えるため。 外すのは表示と絞り込みの時だけで、 保存値は変えない。
 * 全部外れたら「すべて」。 外すものが無ければ同じ選択を返す。
 */
export const keepKnownWorkflows = (
  selected: ReadonlySet<string>,
  options: readonly string[],
): ReadonlySet<string> => {
  const kept = [...selected].filter((id) => options.includes(id))
  return kept.length === selected.size ? selected : new Set(kept)
}
